import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PAY_FREQUENCIES, PAY_FREQUENCY_LABELS, type EmployeeDTO, type EmployeeInput, type PayFrequency, type UserRowDTO } from '@bifeai/shared';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Textarea,
} from '@bifeai/ui';
import { Plus, UserRound } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { centsToPesosInput, pesosToCents } from '../../lib/units';

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });

export function balanceText(e: Pick<EmployeeDTO, 'balanceCents'>) {
  if (e.balanceCents === null) return null;
  if (e.balanceCents > 0) return { label: 'Se le debe', value: formatMoney(e.balanceCents), tone: 'warning' as const };
  if (e.balanceCents < 0) return { label: 'Adelantado', value: formatMoney(-e.balanceCents), tone: 'info' as const };
  return { label: 'Al día', value: formatMoney(0), tone: null };
}

export function StatusBadge({ e }: { e: Pick<EmployeeDTO, 'status' | 'lastAttendanceAt'> }) {
  if (e.status === 'IN') return <Badge tone="success">Adentro desde {e.lastAttendanceAt ? timeOf(e.lastAttendanceAt) : ''}</Badge>;
  if (e.status === 'OUT') return <Badge>Se fue a las {e.lastAttendanceAt ? timeOf(e.lastAttendanceAt) : ''}</Badge>;
  return null;
}

/** Empleados: ficha, saldo (adelantos, sueldo, pagos) y si hoy está trabajando. */
export function EmployeesPage() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['employees'], queryFn: () => api.get<EmployeeDTO[]>('/employees') });
  const { can } = useSession();
  const canSalary = can('employees.manage');
  const [creating, setCreating] = useState(false);
  const rows = q.data ?? [];
  const owed = rows.reduce((a, e) => a + Math.max(0, e.balanceCents ?? 0), 0);
  const inside = rows.filter((e) => e.status === 'IN').length;

  return (
    <>
      <PageHeader
        title="Empleados"
        subtitle={
          rows.length
            ? `Hoy trabajando: ${inside}. ${owed ? `Se les debe ${formatMoney(owed)}.` : ''}`
            : 'Ficha de cada empleado, adelantos, mercadería que se lleva, sueldo y asistencia.'
        }
        actions={
          canSalary && (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              Agregar empleado
            </Button>
          )
        }
      />
      <Callout tone="info">Es control interno: no reemplaza al recibo de sueldo ni a los aportes que lleva el contador.</Callout>
      <Card>
        <CardBody flush>
          {q.isPending ? (
            <div style={{ padding: 16 }}>
              <Skeleton />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<UserRound />}
              title="Todavía no cargaste empleados"
              text="Cargalos con su sueldo; después les anotás adelantos y les pagás."
            />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Empleado</th>
                    <th>Sueldo</th>
                    <th style={{ textAlign: 'right' }}>Adelantos del período</th>
                    <th style={{ textAlign: 'right' }}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => {
                    const b = balanceText(e);
                    return (
                      <tr key={e.id} data-clickable="true" onClick={() => navigate(`/empleados/${e.id}`)} style={{ opacity: e.isActive ? 1 : 0.5 }}>
                        <td>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <Avatar name={e.name} />
                            <div>
                              <span className="bf-cell-main">{e.name}</span> {!e.isActive && <Badge>Inactivo</Badge>} <StatusBadge e={e} />
                              <div className="bf-cell-sub">
                                {[e.position, e.branchName, e.userName && `usuario ${e.userName}`].filter(Boolean).join(' · ')}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {e.salaryCents !== null ? formatMoney(e.salaryCents) : '—'}
                          <div className="bf-cell-sub">{PAY_FREQUENCY_LABELS[e.payFrequency].toLowerCase()}</div>
                        </td>
                        <td className="bf-num" style={{ textAlign: 'right' }}>
                          {e.pendingAdvancesCents ? formatMoney(e.pendingAdvancesCents) : '—'}
                        </td>
                        <td className={`bf-num ${b?.tone === 'warning' ? 'bf-text-warning' : ''}`} style={{ textAlign: 'right', fontWeight: 600 }}>
                          {b ? b.value : '—'}
                          {b && <div className="bf-cell-sub">{b.label}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {creating && (
        <EmployeeFormDialog
          employee={null}
          onClose={(id) => {
            setCreating(false);
            if (id) navigate(`/empleados/${id}`);
          }}
        />
      )}
    </>
  );
}

export function EmployeeFormDialog({ employee, onClose }: { employee: EmployeeDTO | null; onClose: (id?: string) => void }) {
  const qc = useQueryClient();
  const { me, can, multiBranch, isHeadquartersUser } = useSession();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<UserRowDTO[]>('/users'), enabled: can('users.manage') });
  const [f, setF] = useState({
    name: employee?.name ?? '',
    phone: employee?.phone ?? '',
    position: employee?.position ?? '',
    branchId: employee?.branchId ?? me.membership.branchId ?? me.branches.find((b) => b.isHeadquarters)?.id ?? '',
    userId: employee?.userId ?? '',
    startDate: employee?.startDate ?? '',
    payFrequency: (employee?.payFrequency ?? 'MONTHLY') as PayFrequency,
    salary: employee ? centsToPesosInput(employee.salaryCents ?? 0) : '',
    isActive: employee?.isActive ?? true,
    notes: employee?.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (f.name.trim().length < 2) return setError('Escribí el nombre.');
    const body: EmployeeInput = {
      name: f.name.trim(),
      phone: f.phone.trim() || null,
      position: f.position.trim() || null,
      branchId: f.branchId || null,
      userId: f.userId || null,
      startDate: f.startDate || null,
      payFrequency: f.payFrequency,
      salaryCents: pesosToCents(f.salary || '0') ?? 0,
      isActive: f.isActive,
      notes: f.notes.trim() || null,
    };
    setBusy(true);
    setError(null);
    try {
      const res = employee ? await api.put<EmployeeDTO>(`/employees/${employee.id}`, body) : await api.post<EmployeeDTO>('/employees', body);
      await qc.invalidateQueries({ queryKey: ['employees'] });
      onClose(employee ? undefined : res.id);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()} title={employee ? 'Editar empleado' : 'Agregar empleado'} wide>
      <form className="bf-form" onSubmit={submit} noValidate>
        <div className="bf-form-row">
          <Field label="Nombre y apellido">
            <Input value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus />
          </Field>
          <Field label="Puesto (opcional)">
            <Input value={f.position} onChange={(e) => set('position', e.target.value)} placeholder="Carnicero, cajera, despostador…" />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="Teléfono (opcional)">
            <Input inputMode="tel" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label="Entró a trabajar (opcional)">
            <Input type="date" value={f.startDate} onChange={(e) => set('startDate', e.target.value)} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="Cobra">
            <Select value={f.payFrequency} onChange={(e) => set('payFrequency', e.target.value as PayFrequency)}>
              {PAY_FREQUENCIES.map((p) => (
                <option key={p} value={p}>
                  {PAY_FREQUENCY_LABELS[p]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Sueldo acordado" hint="Se propone al liquidar; se puede cambiar cada vez.">
            <Input inputMode="decimal" value={f.salary} onChange={(e) => set('salary', e.target.value)} placeholder="$ 0" />
          </Field>
        </div>
        <div className="bf-form-row">
          {multiBranch && isHeadquartersUser && (
            <Field label="Sucursal">
              <Select value={f.branchId} onChange={(e) => set('branchId', e.target.value)}>
                {me.branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {(users.data?.length ?? 0) > 0 && (
            <Field label="¿Usa el sistema?" hint="Si es cajero o encargado, vinculalo con su usuario.">
              <Select value={f.userId} onChange={(e) => set('userId', e.target.value)}>
                <option value="">No</option>
                {users.data!.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
        <Field label="Notas (opcional)">
          <Textarea rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
        {employee && (
          <label className="bf-check">
            <Switch checked={f.isActive} onCheckedChange={(v) => set('isActive', v)} label="Activo" /> Sigue trabajando
          </label>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-actions">
          <Button onClick={() => onClose()} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            Guardar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
